import { makeObservable, observable, action } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import {
  AUDIO_ARTIFACT_MISSING_MESSAGE,
  classifyFinalizedAudio,
  NO_USABLE_AUDIO_FOUND_MESSAGE,
} from "@/modules/audio-recording/final-audio-validation";
import {
  getMeetingHandoffPreviousAudioRecordingId,
  hasPersistedNonEmptyArtifact,
  hasPersistedTranscriptEvidence,
} from "@/store/audio-recordings/app-events";
import { AudioRecordingUploadStatus } from "@/store/audio-recordings/types";
import { AudioRecordingTranscriptStatus } from "@/store/audio/transcription";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";

import { isTransitionAllowed, STAGE_FAILURE_OUTCOME } from "./audio-pipeline.stages";
import {
  PipelineDiagnostic,
  PipelineSnapshot,
  Stage,
  StageProgress,
  StageStatus,
  TerminalOutcome,
  ValidationResult,
} from "./audio-pipeline.types";
import { resolveTranscriptSource } from "./resolve-transcript-source";
import { runRetranscribeStage } from "./run-retranscribe-stage";
import { runTransformStage } from "./run-transform-stage";
import { runUploadStage } from "./run-upload-stage";
import type { StageResult } from "./stage-result";

type PipelineRecord = {
  stage: Stage;
  stageStatus: StageStatus;
  terminalOutcome: TerminalOutcome | null;
  progress: StageProgress | null;
  diagnostic: PipelineDiagnostic | null;
};

/**
 * Applies a stage adapter's failure outcome to the pipeline and returns
 * false. Returns true on success so the caller can keep walking.
 */
function propagateFailure(
  pipeline: AppStoreAudioPipelineStore,
  audioRecordingId: Uuid,
  result: StageResult
): boolean {
  if (result.ok) return true;
  pipeline.finishWithOutcome(audioRecordingId, result.outcome, result.diagnostic);
  return false;
}

function diagnosticForValidation(result: ValidationResult): PipelineDiagnostic {
  if (result.kind === "no-usable-audio-found") {
    return {
      kind: `no-audio:${result.reason}`,
      message: NO_USABLE_AUDIO_FOUND_MESSAGE,
    };
  }
  if (result.kind === "artifact-missing") {
    return {
      kind: "artifact-missing",
      message: AUDIO_ARTIFACT_MISSING_MESSAGE,
    };
  }
  return { kind: "valid", message: "" };
}

/**
 * Single writer of pipeline state transitions. UI reads `getSnapshot`; workers
 * and stage adapters advance the machine via `startStage`, `completeStage`,
 * and `finishWithOutcome`. Transitions not allowed by `audio-pipeline.stages`
 * throw — the graph is the source of truth.
 */
export class AppStoreAudioPipelineStore extends AppSubStore {
  private readonly recordsById = observable.map<Uuid, PipelineRecord>(undefined, {
    deep: false,
  });
  private readonly activeRuns = new Set<Uuid>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      this,
      | "activeRuns"
      | "recordsById"
      | "writeRecord"
      | "requireRecord"
      | "runPipeline"
      | "runValidateAudioStage"
      | "runResolveTranscriptSourceStage"
      | "getMergeSourceRecordingIds"
      | "persistValidationFailure"
      | "startBackgroundUpload"
      | "startBackgroundUploads"
      | "outcomeFromValidation"
    >(this, {
      activeRuns: false,
      recordsById: observable,
      writeRecord: action,
      onRecordingCompleted: action,
      startProcessing: action,
      initializeAtFinalize: action,
      dispatchRetry: action,
      clearSnapshot: action,
      resetState: action,
      startStage: action,
      completeStage: action,
      finishWithOutcome: action,
      failCurrentStage: action,
      setProgress: action,
      setDiagnostic: action,
      getSnapshot: false,
      requireRecord: false,
      runPipeline: false,
      runValidateAudioStage: false,
      runResolveTranscriptSourceStage: false,
      getMergeSourceRecordingIds: false,
      persistValidationFailure: false,
      startBackgroundUpload: false,
      startBackgroundUploads: false,
      outcomeFromValidation: false,
    });
  }

  getSnapshot(audioRecordingId: Uuid): PipelineSnapshot | null {
    const record = this.recordsById.get(audioRecordingId);
    if (!record) return null;
    return {
      audioRecordingId,
      stage: record.stage,
      stageStatus: record.stageStatus,
      terminalOutcome: record.terminalOutcome,
      progress: record.progress,
      diagnostic: record.diagnostic,
    };
  }

  /**
   * Entrypoint called when a recording finishes. Seeds the pipeline at
   * `Finalize` in the `idle` state, then kicks off the async stage walk.
   */
  onRecordingCompleted({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.startProcessing({ audioRecordingId });
  }

  /**
   * Starts or re-enters the canonical post-recording pipeline from `Finalize`.
   *
   * This is the single manual/automatic entrypoint used by recording
   * completion, boot reconciliation, and explicit "process again" actions.
   */
  startProcessing({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    if (this.activeRuns.has(audioRecordingId)) {
      return;
    }

    this.initializeAtFinalize({ audioRecordingId });
    void this.runPipeline({ audioRecordingId });
  }

  /**
   * Seeds a pipeline record at `Finalize/idle` without starting the async
   * stage walk. Exposed for tests and rehydration paths that want to drive
   * stages manually.
   */
  initializeAtFinalize({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.writeRecord(audioRecordingId, {
      stage: Stage.Finalize,
      stageStatus: "idle",
      terminalOutcome: null,
      progress: null,
      diagnostic: null,
    });
    void logger.debug({
      message: "[audio-pipeline] started",
      info: { audioRecordingId },
    });
  }

  /**
   * Retry the pipeline for a recording based on its terminal outcome. The
   * retry semantics depend on what went wrong:
   *
   * - `no-usable-audio-found` / `audio-artifact-missing`: the pipeline can't fix it;
   *   the UI surfaces "Record again" which creates a new recording.
   * - `upload-failed` / `retranscribe-failed` / `transform-failed`: re-enter
   *   from `Finalize` and let the canonical walk choose the right branch again.
   *
   * This is the only entrypoint UI retry handlers should call.
   */
  dispatchRetry({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    if (this.activeRuns.has(audioRecordingId)) {
      return;
    }

    const record = this.recordsById.get(audioRecordingId);
    if (!record || record.stage !== Stage.Done || record.terminalOutcome === null) return;

    const outcome = record.terminalOutcome;
    if (
      outcome === TerminalOutcome.NoUsableAudioFound ||
      outcome === TerminalOutcome.AudioArtifactMissing ||
      outcome === TerminalOutcome.Success
    ) {
      return;
    }

    // Re-seed at Finalize; the walk picks the right branch based on
    // transcript status. Individual-stage retries collapse into a fresh walk
    // for simplicity — validator still short-circuits no-audio cases, so
    // retry is safe to run from the top.
    this.startProcessing({ audioRecordingId });
  }

  /**
   * Drives the stage machine for a single recording from finalize through
   * done. Each stage either advances the machine or terminates it with a
   * typed outcome. Unexpected exceptions route to UnexpectedFailed so the
   * pipeline always ends in a terminal state.
   */
  private async runPipeline({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    if (this.activeRuns.has(audioRecordingId)) {
      return;
    }

    this.activeRuns.add(audioRecordingId);

    try {
      // Validate
      this.startStage(audioRecordingId, Stage.ValidateAudio);
      const validation = await this.runValidateAudioStage({ audioRecordingId });
      if (validation.kind !== "valid") {
        await this.persistValidationFailure({ audioRecordingId, validation });
        this.finishWithOutcome(
          audioRecordingId,
          this.outcomeFromValidation(validation),
          diagnosticForValidation(validation)
        );
        return;
      }

      // Resolve transcript source
      this.startStage(audioRecordingId, Stage.ResolveTranscriptSource);
      const source = await this.runResolveTranscriptSourceStage({ audioRecordingId });
      const mergeSourceRecordingIds = await this.getMergeSourceRecordingIds({ audioRecordingId });

      if (mergeSourceRecordingIds) {
        const backgroundUploadIds =
          source === "retranscribe"
            ? mergeSourceRecordingIds.filter(
                sourceRecordingId => sourceRecordingId !== audioRecordingId
              )
            : mergeSourceRecordingIds;
        this.startBackgroundUploads({ audioRecordingIds: backgroundUploadIds });
      }

      if (source === "retranscribe") {
        // Upload the source so the backend can retranscribe from it.
        this.startStage(audioRecordingId, Stage.UploadSource);
        const uploadResult = await runUploadStage({
          audioRecordingId,
          uploadingStore: this.store.audio.uploading,
        });
        if (!propagateFailure(this, audioRecordingId, uploadResult)) return;

        // Retranscribe
        this.startStage(audioRecordingId, Stage.RetranscribeFromSource);
        const retranscribeResult = await runRetranscribeStage({ audioRecordingId });
        if (!propagateFailure(this, audioRecordingId, retranscribeResult)) return;
      }

      if (!mergeSourceRecordingIds && source !== "retranscribe") {
        // Local transcript path: upload in the background so the source
        // still lands in cloud, but don't block transform on it.
        this.startBackgroundUpload({ audioRecordingId });
      }

      // Transform
      this.startStage(audioRecordingId, Stage.Transform);
      const transformResult = await runTransformStage({
        audioRecordingId,
        transformingStore: this.store.audio.transforming,
      });
      if (!propagateFailure(this, audioRecordingId, transformResult)) return;

      this.finishWithOutcome(audioRecordingId, TerminalOutcome.Success);
    } catch (error) {
      void logger.error({
        message: "[audio-pipeline] unexpected pipeline failure",
        info: {
          audioRecordingId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
      try {
        this.finishWithOutcome(audioRecordingId, TerminalOutcome.UnexpectedFailed, {
          kind: "unexpected",
          message: error instanceof Error ? error.message : "Unexpected pipeline failure",
        });
      } catch {
        // Pipeline may already be in Done — swallow secondary failure.
      }
    } finally {
      this.activeRuns.delete(audioRecordingId);
    }
  }

  private async runValidateAudioStage({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<ValidationResult> {
    await this.store.audioRecordings.updateObservable(audioRecordingId);
    const recordingObservable = await this.store.audioRecordings.getAsync(audioRecordingId);

    if (!recordingObservable) {
      return { kind: "artifact-missing" };
    }

    const artifactStatus = await this.store.audio.getUploadableArtifactStatus({
      audioRecordingId,
      recordingStrategy: recordingObservable.recordingStrategy,
    });
    const transcriptState = await this.store.audio.transcription.getTranscriptState({
      audioRecordingId,
    });
    const hasPersistedArtifact =
      artifactStatus.hasUploadableArtifact ||
      hasPersistedNonEmptyArtifact({
        modelData: recordingObservable.modelData,
      }) ||
      recordingObservable.uploadStatus === AudioRecordingUploadStatus.COMPLETED;
    const hasTranscriptEvidence =
      transcriptState.status === AudioRecordingTranscriptStatus.Ready ||
      hasPersistedTranscriptEvidence({
        modelData: recordingObservable.modelData,
      });

    return classifyFinalizedAudio({
      modelData: recordingObservable.modelData,
      hasPersistedArtifact,
      hasTranscriptEvidence,
      silenceSnapshot: this.store.audio.recording.getSilenceSnapshotForRecording(audioRecordingId),
    });
  }

  private async runResolveTranscriptSourceStage({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<"local" | "retranscribe"> {
    const transcriptState = await this.store.audio.transcription.getTranscriptState({
      audioRecordingId,
    });
    return resolveTranscriptSource({ transcriptStatus: transcriptState.status });
  }

  private async getMergeSourceRecordingIds({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<Uuid[] | null> {
    const recording =
      this.store.audioRecordings.get(audioRecordingId) ??
      (await this.store.audioRecordings.getAsync(audioRecordingId));
    if (!recording) return null;

    const previousAudioRecordingId = getMeetingHandoffPreviousAudioRecordingId({
      modelData: recording.modelData,
    });
    if (!previousAudioRecordingId) return null;

    return [previousAudioRecordingId, audioRecordingId];
  }

  /**
   * Persists the durable upload status for validate-audio terminals so refresh
   * does not immediately re-enter the same pre-upload failure path.
   */
  private async persistValidationFailure({
    audioRecordingId,
    validation,
  }: {
    audioRecordingId: Uuid;
    validation: Exclude<ValidationResult, { kind: "valid" }>;
  }): Promise<void> {
    const recording = this.store.audioRecordings.get(audioRecordingId);
    if (!recording) {
      return;
    }

    if (
      validation.kind === "no-usable-audio-found" &&
      recording.uploadStatus !== AudioRecordingUploadStatus.COMPLETED &&
      recording.uploadStatus !== AudioRecordingUploadStatus.SKIPPED
    ) {
      await this.store.dexie.repositories.audioRecordings.markAsUploadSkipped({
        id: audioRecordingId,
      });
      return;
    }

    if (
      validation.kind === "artifact-missing" &&
      recording.uploadStatus !== AudioRecordingUploadStatus.COMPLETED &&
      recording.uploadStatus !== AudioRecordingUploadStatus.ERROR
    ) {
      await this.store.dexie.repositories.audioRecordings.markAsUploadErrored({
        id: audioRecordingId,
        errorMessage: diagnosticForValidation(validation).message,
        failureKind: "artifact-missing",
      });
    }
  }

  /**
   * Starts the optional happy-path source upload after local transcript
   * resolution.
   *
   * This intentionally does not carry a cancellation handle. If the recording
   * is deleted while the upload worker is already in flight, the upload may
   * still finish, but persistence is idempotent and the source-upload UI reads
   * directly from upload state rather than pipeline state.
   */
  private startBackgroundUpload({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    const recording = this.store.audioRecordings.get(audioRecordingId);
    if (recording?.uploadStatus === AudioRecordingUploadStatus.COMPLETED) {
      return;
    }

    const uploadProgress = this.store.audio.uploading.getProgress({ audioRecordingId });
    if (uploadProgress.isUploading) {
      return;
    }

    // Fire and forget — background upload progress is surfaced through the
    // uploading store directly (voice source chip) rather than the main
    // pipeline snapshot.
    void this.store.audio.uploading.enqueue({ audioRecordingId });
  }

  private startBackgroundUploads({ audioRecordingIds }: { audioRecordingIds: Uuid[] }): void {
    for (const audioRecordingId of audioRecordingIds) {
      this.startBackgroundUpload({ audioRecordingId });
    }
  }

  private outcomeFromValidation(result: ValidationResult): TerminalOutcome {
    switch (result.kind) {
      case "no-usable-audio-found":
        return TerminalOutcome.NoUsableAudioFound;
      case "artifact-missing":
        return TerminalOutcome.AudioArtifactMissing;
      case "valid":
        return TerminalOutcome.Success;
    }
  }

  /**
   * Transition into a new stage and mark it running. Validates the transition
   * against the stage graph; throws on an illegal move to catch orchestration
   * bugs at the source.
   */
  startStage(audioRecordingId: Uuid, nextStage: Stage): void {
    const record = this.requireRecord(audioRecordingId);
    if (!isTransitionAllowed(record.stage, nextStage)) {
      throw new Error(
        `[audio-pipeline] illegal transition ${record.stage} -> ${nextStage} for ${audioRecordingId}`
      );
    }
    this.writeRecord(audioRecordingId, {
      ...record,
      stage: nextStage,
      stageStatus: "running",
      progress: null,
      diagnostic: null,
    });
    void logger.debug({
      message: "[audio-pipeline] stage started",
      info: { audioRecordingId, stage: nextStage },
    });
  }

  /** Mark the current stage as `done` without leaving it. The caller then
   * either calls `startStage` to advance or `finishWithOutcome` to terminate. */
  completeStage(audioRecordingId: Uuid): void {
    const record = this.requireRecord(audioRecordingId);
    this.writeRecord(audioRecordingId, {
      ...record,
      stageStatus: "done",
      progress: null,
    });
    void logger.debug({
      message: "[audio-pipeline] stage completed",
      info: { audioRecordingId, stage: record.stage },
    });
  }

  /**
   * Finish the pipeline with a terminal outcome. Always transitions to `Done`
   * (validated) and records the outcome. Diagnostics are optional; when the
   * outcome is a failure, callers should provide one for UI and telemetry.
   */
  finishWithOutcome(
    audioRecordingId: Uuid,
    outcome: TerminalOutcome,
    diagnostic: PipelineDiagnostic | null = null
  ): void {
    const record = this.requireRecord(audioRecordingId);
    if (!isTransitionAllowed(record.stage, Stage.Done)) {
      throw new Error(
        `[audio-pipeline] cannot finish from ${record.stage} for ${audioRecordingId}`
      );
    }
    this.writeRecord(audioRecordingId, {
      stage: Stage.Done,
      stageStatus: "done",
      terminalOutcome: outcome,
      progress: null,
      diagnostic,
    });
    void logger.info({
      message: "[audio-pipeline] finished",
      info: {
        audioRecordingId,
        outcome,
        diagnosticKind: diagnostic?.kind ?? null,
        hasDiagnostic: diagnostic !== null,
      },
    });
  }

  /**
   * Convenience for stage workers that throw: map the failure to the stage's
   * default terminal outcome (see `STAGE_FAILURE_OUTCOME`).
   */
  failCurrentStage(audioRecordingId: Uuid, diagnostic: PipelineDiagnostic | null = null): void {
    const record = this.requireRecord(audioRecordingId);
    this.finishWithOutcome(audioRecordingId, STAGE_FAILURE_OUTCOME[record.stage], diagnostic);
  }

  setProgress(audioRecordingId: Uuid, progress: StageProgress | null): void {
    const record = this.requireRecord(audioRecordingId);
    this.writeRecord(audioRecordingId, { ...record, progress });
  }

  setDiagnostic(audioRecordingId: Uuid, diagnostic: PipelineDiagnostic | null): void {
    const record = this.requireRecord(audioRecordingId);
    this.writeRecord(audioRecordingId, { ...record, diagnostic });
  }

  clearSnapshot({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.recordsById.delete(audioRecordingId);
  }

  resetState(): void {
    this.recordsById.clear();
    this.activeRuns.clear();
  }

  private writeRecord(audioRecordingId: Uuid, record: PipelineRecord): void {
    this.recordsById.set(audioRecordingId, record);
  }

  private requireRecord(audioRecordingId: Uuid): PipelineRecord {
    const record = this.recordsById.get(audioRecordingId);
    if (!record) {
      throw new Error(
        `[audio-pipeline] no pipeline record for ${audioRecordingId}; call startProcessing first`
      );
    }
    return record;
  }
}
